import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import sumBy from 'lodash/sumBy';
import partition from 'lodash/partition';
import moment from 'moment-timezone';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import memoize from 'lodash/memoize';

import {truncateMiddle} from 'common/util/string';
import {NON_INTERVALIZED_VALUE, RetentionBookmarkParams} from 'common/report/util/retention/types';
import {serializePropFilter} from 'common/report/util/arb';
import {FormattedRow, RawCell, RetentionType} from 'common/report/util/retention/types';
import {
  Addiction,
  isUnsegmentedData,
  KeyedByDate,
  KeyedBySegment,
  Retention,
  RetentionData,
  SegmentKeyToPath,
  SuccessResponse,
} from 'common/report/data/retention/client';
import {AGGREGATED_ROW_NAME} from 'common/report/util/retention/constants';
import {RetentionQuery} from 'common/report/data/retention/retention-query';
import {getChartSegmentColorCombo} from 'common/util/colors';
import {formatNumber, formatPercent} from 'common/report/util/retention/util';
import {parseDateSortKey, parseNumberRangeSortKey} from 'common/util/header-sorting';
import {PropertyType} from 'common/report/constants';
import {Segment} from 'common/widgets/query-block/segment-menu/types';
import {RetentionApiChartTypes} from 'common/report/util/retention/constants';
import {TrendDataTableRow} from 'common/widgets/trend-data-table/types';

import {getFlattenedSegmentType, isSegmented} from './format';

const MAX_TITLE_CELL_LENGTH = 60;

export type BaseRow = {
  date: Date;
  cohort_size: number;
  data: number[];
  complete_date: Array<Date | null>;
};

export type SegmentRow = BaseRow & {
  segment: string;
};

export type Row = BaseRow | SegmentRow;
/** Aggregate row: data + optional segment, but no date */
export type AverageRow = Omit<BaseRow, 'date'> & {
  segment?: string;
  /** not calculated the same way as normal rows */
  percent_data?: number[];
};

export type SuperRow = Row | AverageRow;

/** A collection of dated rows and their aggregate, belonging to a segment. */
export type RowWithAggregate = {
  /** An aggregate of all of the rows in datedRows. */
  aggregate: AverageRow;
  /** Collection of rows for this segment, ordered by birth interval. */
  datedRows: Row[];
  /** If empty, represents unsegmented data. */
  segment?: string;
};

export function isSegmentRow(row: SuperRow): row is SegmentRow {
  return `segment` in row && `date` in row;
}

export function isAverageRow(row: SuperRow): row is AverageRow {
  return !(`date` in row);
}

export function isIntervalRow(row: SuperRow): row is BaseRow {
  return !(`segment` in row) && `date` in row;
}

const WITH_YEAR = `MMM D, YYYY`;
const WITHOUT_YEAR = `MMM D`;
export const formatDate = memoize(function (date: Date, timezone: string, includeYear = true): string {
  return moment.tz(date, timezone).format(includeYear ? WITH_YEAR : WITHOUT_YEAR);
});

function getRowHead(row: SuperRow, retentionType: RetentionType, timezone: string, isSubRow) {
  if (`segment` in row && !isSubRow) {
    // prefer segment even for average rows
    return stringifySegment(row.segment);
  } else if (`date` in row) {
    return formatDate(row.date, timezone);
  } else {
    // only use this when no segment is available
    return AGGREGATED_ROW_NAME(retentionType);
  }
}

export function getCellsForRow(retentionType: RetentionType, timezone: string) {
  return function getCellsForRow(row: SuperRow, idx: number, isSubRow = false): FormattedRow<RawCell> {
    const data = row.data.map(getCellForIndex(row, timezone));
    const rowHead = getRowHead(row, retentionType, timezone, isSubRow);
    const truncatedHead =
      rowHead.length > MAX_TITLE_CELL_LENGTH ? truncateMiddle(rowHead, MAX_TITLE_CELL_LENGTH) : rowHead;

    return {
      cohort_size: row.cohort_size,
      data,
      display_cohort_size: formatNumber(row.cohort_size),
      head: rowHead,
      display_head: truncatedHead,
      raw_head: rowHead,
      selection_color: getChartSegmentColorCombo(idx).primary,
    };
  };
}

function stringifySegment(seg: string | string[]): string {
  if (Array.isArray(seg)) {
    return seg.join(` / `);
  } else {
    return seg;
  }
}

export function getCellForIndex(row: SuperRow, timezone: string) {
  return function getCellForIndex(value: number, valIdx: number): RawCell {
    const completeDate = row.complete_date[valIdx];
    const incomplete = !!completeDate;
    let enteredValue = null;

    if (`segment` in row) {
      enteredValue = stringifySegment(row.segment);
    } else if (isAverageRow(row)) {
      enteredValue = `$average`; // this doesn't get consumed anywhere
    } else {
      enteredValue = formatDate(row.date, timezone);
    }

    let cohortSize: number, percent: number;
    if (`percent_data` in row) {
      percent = row.percent_data[valIdx];
      cohortSize = percent > 0 ? Math.round(value / percent) : 0;
    } else {
      cohortSize = row.cohort_size;
      percent = cohortSize > 0 ? value / cohortSize : 0;
    }

    return {
      date: `date` in row ? row.date : null,
      cohort_size: cohortSize,
      complete_date: incomplete && formatDate(completeDate, timezone, false),
      incomplete,
      percent,
      value,
      percent_value: formatPercent(percent),
      percent_drop_off_value: formatPercent(1 - percent),
      absolute_value: formatNumber(value),
      absolute_drop_off_value: cohortSize - value,
      selectable: true,
      entered_value: enteredValue,
    };
  };
}

export const DEFAULT_UNSEG_BUCKET = `Overall`;

// TODO (Ange): Delete once custom buckets is released and approved
function showTimeUpdates() {
  return true;
}

/**
 * Turn a collection of RowWithAggregates into a flat list of rows.
 *
 * For unsegmented retention and frequency, explode the single RowWithAggregate into
 * a row for the aggregate and rows for all the birth intervals.
 *
 * For each segment of segmented retention and frequency, choose the aggregate row.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function mapToSortedUiRows(
  rowData: RowWithAggregate[],
  timezone: string,
  bookmarkParams: RetentionBookmarkParams,
): Array<FormattedRow<RawCell>> {
  const rows = (function (): Array<FormattedRow<RawCell>> {
    const rowToCell = getCellsForRow(bookmarkParams.retention_type, timezone);

    if (
      bookmarkParams.chart_type === RetentionApiChartTypes.Table ||
      bookmarkParams.chart_type === RetentionApiChartTypes.Line
    ) {
      if (showTimeUpdates()) {
        return rowData.map((group, idx) => {
          const averageRow = group.aggregate;
          const fmtRow = rowToCell(averageRow, idx);

          if (isSegmented(bookmarkParams.group_by)) {
            fmtRow.raw_head = fmtRow.display_head = fmtRow.head = stringifySegment(group.aggregate.segment);
          }

          if (bookmarkParams.date_unit !== NON_INTERVALIZED_VALUE) {
            fmtRow.display_cohort_size = `100%`;
            fmtRow.groupedRows = group.datedRows.map((groupedRow, idx) => rowToCell(groupedRow, idx, true));
          }

          return fmtRow;
        });
      }

      // TODO (Ange): Delete once custom buckets is released and approved
      if (isSegmented(bookmarkParams.group_by)) {
        return rowData.map((group, idx) => {
          const averageRow = group.aggregate;
          const fmtRow = rowToCell(averageRow, idx);
          fmtRow.raw_head = fmtRow.display_head = fmtRow.head = stringifySegment(group.aggregate.segment);
          return fmtRow;
        });
      } else {
        const [row] = rowData;
        return [
          {
            ...rowToCell(row.aggregate, 0),
            display_cohort_size: `100%`,
          },
          ...row.datedRows.map((row, idx) => rowToCell(row, idx + 1)),
        ];
      }
    } else if (bookmarkParams.chart_type === RetentionApiChartTypes.RetentionTrend) {
      if (isSegmented(bookmarkParams.group_by)) {
        return rowData.map((segmentRow, idx) => {
          const data = segmentRow.datedRows.reduce((data, dr) => {
            if (dr.data.length > bookmarkParams.retention_bucket) {
              data.push(
                getCellForIndex(dr, timezone)(
                  dr.data[bookmarkParams.retention_bucket],
                  bookmarkParams.retention_bucket,
                ),
              );
            }
            return data;
          }, []);
          const segment = stringifySegment(segmentRow.aggregate.segment);
          return {
            cohort_size: 0,
            display_cohort_size: `0`,
            data,
            display_head: segment,
            head: segment,
            raw_head: segment,
            selection_color: getChartSegmentColorCombo(idx).primary,
          };
        });
      } else {
        const [row] = rowData;
        return [
          {
            cohort_size: row.aggregate.cohort_size,
            display_cohort_size: formatNumber(row.aggregate.cohort_size),
            display_head: DEFAULT_UNSEG_BUCKET,
            head: DEFAULT_UNSEG_BUCKET,
            raw_head: DEFAULT_UNSEG_BUCKET,
            selection_color: getChartSegmentColorCombo(0).primary,
            data: row.datedRows.reduce((data, datedRow) => {
              if (datedRow.data.length > bookmarkParams.retention_bucket) {
                data.push(
                  getCellForIndex(datedRow, timezone)(
                    datedRow.data[bookmarkParams.retention_bucket],
                    bookmarkParams.retention_bucket,
                  ),
                );
              }
              return data;
            }, []),
          },
        ];
      }
    } else {
      return [];
    }
  })();

  return isSegmented(bookmarkParams.group_by)
    ? sortTableRows(
        rows,
        bookmarkParams.sort_column === `cohort`,
        bookmarkParams.sort_desc,
        getFlattenedSegmentType(bookmarkParams.group_by),
      )
    : rows;
}

export function mapToTrendDataTableRows(
  rowData: RowWithAggregate[],
  {
    timezone,
    bookmark,
    numSegments,
    segmentKeyToPath,
  }: {timezone: string; bookmark: RetentionBookmarkParams; numSegments: number; segmentKeyToPath: SegmentKeyToPath},
): TrendDataTableRow[] {
  const formattedRows = mapToSortedUiRows(rowData, timezone, bookmark);
  const selectedSegmentSet = new Set(bookmark.selected_segments);
  return formattedRows.map((row) => {
    const segmentValues = numSegments > 1 ? segmentKeyToPath[row.display_head] : [row.display_head];
    return {
      rowId: row.raw_head,
      selectionColor: row.selection_color,
      isSelected: selectedSegmentSet.has(row.raw_head),
      segmentValues,
      data: row.data.map((d) => {
        return {
          timestamp: d.date.valueOf(),
          value: bookmark.percentage_view ? d.percent : d.value,
        };
      }),
    };
  });
}

export function filtersToArbString(propertyFilters: RetentionQuery['bornFilters'] | undefined) {
  if (!propertyFilters) {
    return null;
  }

  return propertyFilters.map((pf) => serializePropFilter(pf)).join(` and `);
}

function mapToCompleteDates(
  incompleteBuckets: Record<string, string[]>,
  date: string,
  tz: string,
  fallbackLength: number,
): Array<Date | null> {
  if (incompleteBuckets && incompleteBuckets[date]) {
    return incompleteBuckets[date].map((maybeDate) =>
      typeof maybeDate === `string` ? moment.tz(maybeDate, tz).toDate() : null,
    );
  } else {
    return new Array(fallbackLength).fill(null);
  }
}

export function sortTableRows<T extends {head: string | Date; cohort_size: number}>(
  rows: T[],
  columnBySize: boolean,
  desc: boolean,
  segmentType: PropertyType,
): T[] {
  rows = sortBy(rows, (row) => {
    if (segmentType === PropertyType.Number) {
      return parseNumberRangeSortKey(row.head as string, {sortOrder: desc ? -1 : 1});
    } else if (segmentType === PropertyType.Datetime) {
      return parseDateSortKey(row.head.toString());
    } else {
      return row.head;
    }
  });
  if (columnBySize) {
    rows = sortBy(rows, (row) => row.cohort_size);
  }

  if (desc) {
    rows.reverse();
  }
  return rows;
}

/**
 * Choose a new set of segments for the data.
 *
 * If some were previously selected, keep them. If none of the previous selections
 * exist in the data, select new ones.
 *
 * @param fillRemaining if there is space left after preserving selected segments, select some more
 * @param trimToSize if there are more saved segments than numSegments, slice them to the appropraiate length
 */
export function chooseSelectedSegments({
  savedSegments,
  data,
  isSegmented,
  numSegments,
  fillRemaining,
  trimToSize,
}: {
  data: FormattedRow[];
  savedSegments: string[];
  isSegmented: boolean;
  numSegments: number;
  fillRemaining: boolean;
  trimToSize: boolean;
}): string[] {
  const actualDataSegments = new Set(data.map((row) => row.raw_head));

  let selections: string[] = [];

  if (savedSegments.length) {
    const stillValidSegments = savedSegments.filter((saved) => actualDataSegments.has(saved));

    if (stillValidSegments.length > 0) {
      selections = trimToSize ? stillValidSegments.slice(0, numSegments) : stillValidSegments;

      if (fillRemaining && selections.length < numSegments) {
        for (const seg of actualDataSegments) {
          if (!selections.includes(seg)) {
            selections.push(seg);
          }

          if (selections.length === numSegments) {
            break;
          }
        }
      }

      return selections;
    }
  }

  if (!isSegmented) {
    // for unsegmented, just choose the average row, which will be in position 0
    return [data[0].raw_head];
  }

  return data.slice(0, numSegments).map((d) => d.raw_head);
}

export function chooseExpandedSegments({data, query}: {data: FormattedRow[]; query: RetentionQuery}) {
  if (!query.isSegmented()) {
    // for unsegmented, expand the average row
    return [data[0].raw_head];
  } else {
    const expandedSegmentSet = new Set(query.getExpandedSegments());
    return data.map((d) => d.raw_head).filter((segmentName) => expandedSegmentSet.has(segmentName));
  }
}

/** For calculating row indices and offsets: is there an Average row showing? */
export function isAverageRowShowing(bookmarkParams: RetentionBookmarkParams) {
  return !isSegmented(bookmarkParams.group_by);
}

export function hasRetentionData(formattedRowData: RowWithAggregate[]) {
  return formattedRowData.some((row) =>
    row.datedRows.some((datedRow) => datedRow.cohort_size > 0 || datedRow.data.some((v) => v > 0)),
  );
}

function sortByAscendingDate(a: Row, b: Row) {
  return a.date.valueOf() - b.date.valueOf();
}

/**
 * Transform the API response format into Rows where
 * - dates are aligned to start of day in project time
 * - segments are renamed
 * - rows are sorted by date or size
 * - retention and addiction data looks the same
 * - aggregate data is joined to the segment it represents
 **/
export function getFormattedRowData(
  data: RetentionData,
  metadata: SuccessResponse['metadata'],
  segmentGroups: Segment[],
  timezone: string,
): RowWithAggregate[] {
  if (!data || isEmpty(data)) {
    return [];
  }

  if (isUnsegmentedData(data, !isSegmented(segmentGroups))) {
    const rows = map(omit(data, `$average`), function (row, date) {
      const dateInProjectTime = moment.tz(date, timezone).toDate();
      const numbers = getRowData(row);
      return {
        ...numbers,
        date: dateInProjectTime,
        complete_date: mapToCompleteDates(metadata.incomplete_buckets, date, timezone, numbers.data?.length),
      };
    });

    rows.sort(sortByAscendingDate);

    let averageRow: AverageRow;
    if (`$average` in data) {
      const numbers = getRowData(data[`$average`]);
      averageRow = {
        ...numbers,
        complete_date: mapToCompleteDates(metadata.incomplete_buckets, `$average`, timezone, numbers.data?.length),
        percent_data: computeAveragePercentages(rows),
      };
    }

    return [
      {
        aggregate: averageRow,
        datedRows: rows,
      },
    ];
  }

  // segmented
  return constructMultiDateRowGroups(data, metadata, timezone);
}

/**
 * Collect segmented rows across birth intervals into a single RowWithAggregate per segment.
 */
function constructMultiDateRowGroups(
  data: KeyedByDate<KeyedBySegment<any>>,
  metadata: SuccessResponse['metadata'],
  projectTimezone: string,
): RowWithAggregate[] {
  const segmentMap = new Map<string, RowWithAggregate>();
  const incompleteBuckets = metadata.incomplete_buckets;

  for (const [bucket, , ...path] of traverseLeaves(pick(data, `$average`))) {
    const segment = path[0];
    const numbers = getRowData(bucket);

    segmentMap.set(segment, {
      aggregate: {
        ...numbers,
        segment,
        complete_date: mapToCompleteDates(incompleteBuckets, `$average`, projectTimezone, numbers.data.length),
      },
      datedRows: [],
      segment,
    });
  }
  for (const [bucket, date, ...path] of traverseLeaves(omit(data, `$average`))) {
    const segment = path[0];
    const numbers = getRowData(bucket);

    segmentMap.get(segment).datedRows.push({
      ...numbers,
      segment,
      date: moment.tz(date, projectTimezone).toDate(),
      complete_date: mapToCompleteDates(incompleteBuckets, date, projectTimezone, numbers.data.length),
    });
  }

  // fill in averages
  for (const segment of segmentMap.keys()) {
    const rowWithAggregate = segmentMap.get(segment);
    rowWithAggregate.aggregate.percent_data = computeAveragePercentages(rowWithAggregate.datedRows);
  }

  const groups = [...segmentMap.values()].sort(function (a, b) {
    return b.aggregate.cohort_size - a.aggregate.cohort_size;
  });

  for (const group of groups) {
    group.datedRows.sort(sortByAscendingDate);
  }

  return groups;
}

// Local helper functions

export function traverseLeaves(
  tree: KeyedByDate<KeyedBySegment<Addiction>>,
): Generator<[Addiction, string, string], void, void>;
export function traverseLeaves(
  tree: KeyedByDate<KeyedBySegment<Retention>>,
): Generator<[Retention, string, string], void, void>;

/** iteratively yield the leaves (retention or addiction data) of the response tree with their path. */
export function* traverseLeaves<TBucket>(tree: any): Generator<[TBucket, string, string], void, void> {
  function* recurse(tree: any, prefix: string[]) {
    if (isLeaf(tree)) {
      yield [tree, ...prefix];
    } else {
      for (const key of Object.keys(tree)) {
        yield* recurse(tree[key], [...prefix, key]);
      }
    }
  }

  yield* recurse(tree, []);
}

function isLeaf(value: any) {
  if (Array.isArray(value)) {
    return true;
  }

  if (typeof value === `object` && `counts` in value && `first` in value && Array.isArray(value.counts)) {
    return true;
  }

  return false;
}

function getRowData(bucket: Addiction | Retention): Pick<BaseRow, 'cohort_size' | 'data'> {
  if (Array.isArray(bucket)) {
    return {
      cohort_size: bucket[0],
      data: bucket.slice(1),
    };
  } else {
    return {
      cohort_size: bucket.first,
      data: bucket.counts,
    };
  }
}

function rowHasDataAtIndex(i: number) {
  return function (row: Row) {
    return row.data.length > i;
  };
}

/** Construct an aggregate Row from all of the date interval Rows */
function computeAveragePercentages(rows: Row[]): number[] {
  const percents = [];

  const maxIntervals = Math.max(...rows.map((r) => r.data.length));

  for (let columnIndex = 0; columnIndex < maxIntervals; columnIndex++) {
    const rowsWithData = rows.filter(rowHasDataAtIndex(columnIndex));
    const [incompleteRows, completeRows] = partition(rowsWithData, (row) => row.complete_date[columnIndex]);

    const allRowsIncomplete = completeRows.length === 0;
    const _countableRows = allRowsIncomplete ? incompleteRows : completeRows;
    const retainedTotal = sumBy(_countableRows, (row) => row.data[columnIndex]);
    const cohortTotal = sumBy(_countableRows, (row) => row.cohort_size);

    const percent = cohortTotal > 0 ? retainedTotal / cohortTotal : 0;
    percents.push(percent);
  }
  return percents;
}
